/**
 * Shared resolve-payment dialog and flag badge.
 * Used by both PaymentsPage (triage view) and PaymentsPanel (student detail).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useResolvePayment } from '../../hooks/usePayments';
import { useStudentSearch } from '../../hooks/useStudents';

// ---------------------------------------------------------------------------
// Flag badge
// ---------------------------------------------------------------------------
export function FlagBadge({ flag }) {
  const { t } = useTranslation();
  if (!flag) return null;
  const styles = {
    travel_flag:         'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    overpayment:         'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    potential_duplicate: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    unmatched:           'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${styles[flag] || 'bg-gray-100 text-gray-600'}`}>
      <AlertTriangle className="w-3 h-3" />
      {t(`payments.flag.${flag}`, flag)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Student search combobox
// ---------------------------------------------------------------------------
function StudentCombobox({ onSelect }) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const { data } = useStudentSearch(term);
  const students = data?.results ?? data ?? [];

  return (
    <div className="space-y-2">
      <Input
        placeholder={t('payments.resolve.searchStudents')}
        value={term}
        onChange={e => setTerm(e.target.value)}
      />
      {term.length >= 2 && students.length > 0 && (
        <div className="border rounded-lg overflow-hidden divide-y divide-border max-h-48 overflow-y-auto">
          {students.map(s => (
            <button
              key={s.uuid}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 transition-colors"
              onClick={() => onSelect(s)}
            >
              <span className="font-medium">{s.full_name || `${s.first_name} ${s.last_name}`}</span>
              <span className="ml-2 text-xs text-gray-400">{s.student_id}</span>
            </button>
          ))}
        </div>
      )}
      {term.length >= 2 && students.length === 0 && (
        <p className="text-xs text-gray-500 px-1">{t('payments.page.noResults')}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resolution dialog
// ---------------------------------------------------------------------------
export function ResolvePaymentDialog({ payment, onClose }) {
  const { t } = useTranslation();
  const [confirmError, setConfirmError] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const flag = payment.import_flag;

  const resolveMutation = useResolvePayment({
    onSuccess: (data, variables) => {
      if (variables.action === 'assign') {
        setSuccessMsg(t('payments.resolve.assignedSuccess', { name: data.student_name || '' }));
      } else if (variables.action === 'resolve' && flag === 'potential_duplicate') {
        setSuccessMsg(t('payments.resolve.keptSuccess'));
      } else {
        setSuccessMsg(t('payments.resolve.resolvedSuccess'));
      }
      setTimeout(onClose, 1200);
    },
  });

  const handleAssign = (student) => {
    resolveMutation.mutate({ uuid: payment.uuid, action: 'assign', student_uuid: student.uuid });
  };

  const handleResolve = () => {
    resolveMutation.mutate({ uuid: payment.uuid, action: 'resolve' });
  };

  const handleError = () => {
    if (!confirmError) { setConfirmError(true); return; }
    resolveMutation.mutate({ uuid: payment.uuid, action: 'error' });
  };

  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IE', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  const formatAmount = (a) => `€${parseFloat(a || 0).toFixed(2)}`;

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('payments.resolve.title')}</DialogTitle>
        </DialogHeader>

        {successMsg ? (
          <div className="py-6 flex flex-col items-center gap-2 text-green-600">
            <CheckCircle className="w-8 h-8" />
            <p className="text-sm font-medium">{successMsg}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Payment summary */}
            <div className="bg-muted/40 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('payments.page.col.date')}</span>
                <span>{formatDate(payment.payment_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('payments.page.col.amount')}</span>
                <span className="font-semibold">{formatAmount(payment.display_amount ?? payment.amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('payments.page.col.method')}</span>
                <span>{t(`payments.method.${payment.payment_method}`, payment.payment_method)}</span>
              </div>
              {payment.reference_number && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('payments.page.col.reference')}</span>
                  <span className="font-mono text-xs">{payment.reference_number}</span>
                </div>
              )}
              {payment.notes && (
                <div className="pt-1 border-t">
                  <p className="text-xs text-gray-500 break-all">{payment.notes}</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <FlagBadge flag={flag} />
            </div>

            {/* Flag-specific context notes */}
            {flag === 'travel_flag' && (
              <p className="text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 rounded p-2">
                {t('payments.resolve.travelNote')}
              </p>
            )}
            {flag === 'overpayment' && (
              <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded p-2">
                {t('payments.resolve.overpaymentNote', {
                  paid: formatAmount(payment.display_amount ?? payment.amount),
                  diff: '—',
                })}
              </p>
            )}
            {flag === 'potential_duplicate' && (
              <div className="text-xs text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 rounded p-2 space-y-1">
                <p>{t('payments.resolve.duplicateNote')}</p>
                <p className="font-medium">{t('payments.resolve.duplicateKeepNote')}</p>
              </div>
            )}

            {/* Actions per flag type */}
            {flag === 'unmatched' && (
              <div className="space-y-3">
                <p className="text-sm font-medium">{t('payments.resolve.assignStudent')}</p>
                <StudentCombobox onSelect={handleAssign} />
                <div className="pt-2 border-t">
                  {confirmError ? (
                    <div className="space-y-2">
                      <p className="text-xs text-red-600">{t('payments.resolve.markErrorConfirm')}</p>
                      <div className="flex gap-2">
                        <Button variant="destructive" size="sm" onClick={handleError} disabled={resolveMutation.isPending}>
                          {t('payments.resolve.markError')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirmError(false)}>
                          {t('common.cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleError}>
                      {t('payments.resolve.markError')}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {(flag === 'travel_flag' || flag === 'overpayment') && (
              <div className="flex justify-end">
                <Button onClick={handleResolve} disabled={resolveMutation.isPending}>
                  {t('payments.resolve.markResolved')}
                </Button>
              </div>
            )}

            {flag === 'potential_duplicate' && (
              <div className="flex gap-2 justify-end">
                {confirmError ? (
                  <>
                    <p className="text-xs text-red-600 self-center">{t('payments.resolve.markErrorConfirm')}</p>
                    <Button variant="destructive" size="sm" onClick={handleError} disabled={resolveMutation.isPending}>
                      {t('payments.resolve.markError')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setConfirmError(false)}>
                      {t('common.cancel')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={handleError} className="text-red-600 border-red-200 hover:bg-red-50">
                      {t('payments.resolve.markError')}
                    </Button>
                    <Button onClick={handleResolve} disabled={resolveMutation.isPending}>
                      {t('payments.resolve.keepPayment')}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
